/**
 * Cargador dinámico de temas
 * 
 * Este módulo se encarga de cargar el tema correcto basándose en la
 * variable de entorno VITE_APP_BRAND. Si no se especifica, carga el tema 'default'.
 * 
 * El uso de import() dinámico permite que Vite solo incluya el código
 * del tema seleccionado en el bundle final, optimizando el tamaño.
 */

import { ITheme } from './theme.types';

// Obtener la marca desde el parámetro de URL 'tenant', o desde las variables de entorno de Vite
const urlParams = new URLSearchParams(window.location.search);
const BRAND = urlParams.get('tenant') || import.meta.env.VITE_APP_BRAND || 'default';

let themePromise: Promise<ITheme> | null = null;

const themes = import.meta.glob<{ default: ITheme }>('./*/theme.ts');

/**
 * Carga el tema correspondiente a la marca configurada
 * 
 * @returns Promise que resuelve con el objeto ITheme
 * @throws Error si el tema no puede ser cargado
 */
export const loadTheme = async (): Promise<ITheme> => {
  if (!themePromise) {
    // Busca el importador correspondiente según la marca
    const importer = themes[`./${BRAND}/theme.ts`] ?? themes['./default/theme.ts'];

    if (!importer) {
      console.warn(`⚠️ No se encontró el tema para '${BRAND}', usando el tema por defecto.`);
      return (await themes['./default/theme.ts']!()).default as ITheme;
    }

    themePromise = importer()
      .then((module: any) => module.default as ITheme)
      .catch(async (error) => {
        console.error(`❌ Error al cargar el tema '${BRAND}', cargando tema por defecto:`, error);
        const fallback = await themes['./default/theme.ts']!();
        return fallback.default as ITheme;
      });
  }

  return themePromise;
};

/**
 * Exporta el nombre de la marca activa
 */
export const getActiveBrand = (): string => BRAND;

/**
 * Re-exportar los tipos para facilitar su uso
 */
export type { ITheme, IThemeColors, IThemeTypography, IThemeAssets, IThemeContent } from './theme.types';
